/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "AI Decisioning configuration", description = "Configure shared AI Decisioning settings, individual agents, and messages", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "AI Decisioning configuration",
        description: "Configure shared AI Decisioning settings, individual agents, and messages",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Shared configuration",
          "depth": 0,
          "id": "shared-configuration"
        }, {
          "value": "Control channel availability and eligibility",
          "depth": 1,
          "id": "control-channel-availability-and-eligibility"
        }, {
          "value": "Set cross-agent scheduling rules",
          "depth": 1,
          "id": "set-cross-agent-scheduling-rules"
        }, {
          "value": "Resolve conflicts between agents",
          "depth": 1,
          "id": "resolve-conflicts-between-agents"
        }, {
          "value": "Define data available to agents",
          "depth": 1,
          "id": "define-data-available-to-agents"
        }, {
          "value": "Configure send tracking",
          "depth": 1,
          "id": "configure-send-tracking"
        }, {
          "value": "Review configuration changes",
          "depth": 1,
          "id": "review-configuration-changes"
        }, {
          "value": "Agent configuration",
          "depth": 0,
          "id": "agent-configuration"
        }, {
          "value": "Control who the agent can evaluate",
          "depth": 1,
          "id": "control-who-the-agent-can-evaluate"
        }, {
          "value": "Define goals and select data",
          "depth": 1,
          "id": "define-goals-and-select-data"
        }, {
          "value": "Control what data is sent downstream",
          "depth": 1,
          "id": "control-what-data-is-sent-downstream"
        }, {
          "value": "Set agent-specific delivery limits",
          "depth": 1,
          "id": "set-agent-specific-delivery-limits"
        }, {
          "value": "Message configuration",
          "depth": 0,
          "id": "message-configuration"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      img: "img",
      h4: "h4",
      ol: "ol",
      code: "code"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins and marketers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["Access to ", _jsx(_components.a, {
                    href: "/ai-decisioning/overview",
                    children: "AI Decisioning"
                  })]
                }), _jsx("li", {
                  children: "At least one agent created"
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Configure settings that apply across AI Decisioning, to individual agents, and to individual messages."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "AI Decisioning settings are configured at three levels:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Shared configuration"
          }), " applies across agents or provides resources that multiple agents can use. Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Configuration"
          }), " to access it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Agent configuration"
          }), " controls how an individual agent evaluates users, optimizes, and sends messages. Open the agent's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Message configuration"
          }), " controls behavior specific to an individual message. Open the message's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Use the most broadly applicable level for a rule. For example, configure an SMS opt-in rule at the channel level when it should apply to every agent using SMS, rather than recreating the rule for each agent."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "shared-configuration",
        children: "Shared configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "AI Decisioning > Configuration"
        }), " to manage settings that apply across agents."]
      }), "\n", _jsx(_components.h3, {
        id: "control-channel-availability-and-eligibility",
        children: "Control channel availability and eligibility"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Channels"
        }), " to control which destinations agents can use and which users are eligible to receive messages through each channel. Each channel row shows its destination, configuration, eligibility, and send event."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-channels-tab.png",
          alt: "Active channels in configuration UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Channel eligibility is useful for rules that should apply everywhere the channel is used, such as SMS opt-in or regional restrictions. A user must also satisfy any applicable agent- and message-level eligibility rules."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-channel-eligibility-edit.png",
          alt: "Edit channel eligibility"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-cross-agent-scheduling-rules",
        children: "Set cross-agent scheduling rules"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Scheduling"
        }), " for frequency limits and quiet hours that should apply across agents."]
      }), "\n", _jsx(_components.p, {
        children: "You can configure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Agent frequency limits"
          }), " — Limit how often a user can receive messages from each agent."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Quiet hours"
          }), " — Prevent messages during specific times, configured per channel and evaluated using each user's local time zone."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When multiple frequency limits apply, a send must satisfy all of them. You can also set frequency, scheduling, and time zone rules for a single agent on its ", _jsx(_components.a, {
          href: "#set-agent-specific-delivery-limits",
          children: "Configuration tab"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "resolve-conflicts-between-agents",
        children: "Resolve conflicts between agents"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Agent prioritization"
        }), " when the same user can qualify for multiple agents during the same evaluation window."]
      }), "\n", _jsxs(_components.p, {
        children: ["Set each agent's ", _jsx(_components.strong, {
          children: "weight"
        }), " and ", _jsx(_components.strong, {
          children: "bias"
        }), " to determine which agent takes precedence. Use simulated performance to evaluate the expected effect before applying changes."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-agent-prioritization-tab.png",
          alt: "Agent prioritization tab in configuration UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "define-data-available-to-agents",
        children: "Define data available to agents"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Feature Matrix"
        }), " to define which warehouse attributes and behavioral data AI Decisioning can use for evaluation and analysis."]
      }), "\n", _jsxs(_components.p, {
        children: ["Feature matrices are shared resources, but each agent selects the feature matrix it uses. The matrix must match the agent's parent model. For more information, see ", _jsx(_components.a, {
          href: "/ai-decisioning/user-feature-matrix",
          children: "User feature matrix"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-feature-matrix.png",
          alt: "Feature Matrix tab listing existing feature matrices"
        })
      }), "\n", _jsx(_components.h4, {
        id: "create-a-feature-matrix",
        children: "Create a feature matrix"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Configuration > Feature Matrix"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "New feature matrix"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the parent model."
        }), "\n", _jsx(_components.li, {
          children: "Enter a name."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create feature matrix"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the columns AI Decisioning should use."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-example-feature-matrix.png",
          alt: "Feature matrix detail view showing models and source columns"
        })
      }), "\n", _jsx(_components.h4, {
        id: "generate-a-feature-matrix-with-ai",
        children: "Generate a feature matrix with AI"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "AI-generated feature matrices"
      }), "\n", _jsxs(_components.p, {
        children: ["Instead of selecting columns manually, select ", _jsx(_components.strong, {
          children: "Generate with AI"
        }), " to draft a feature matrix for an agent. Hightouch drafts features from the agent's parent-model schema and tailors them to the agent's goal. Generation runs in the background and can take a few minutes."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-generate-feature-matrix.png",
          alt: "Generate feature matrix dialog"
        })
      }), "\n", _jsx(_components.p, {
        children: "Review generated features before using them. Confirm they're relevant to the agent's goal and that none rely on data your compliance policies restrict."
      }), "\n", _jsx(_components.h3, {
        id: "configure-send-tracking",
        children: "Configure send tracking"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Send event tracking"
      }), "\n", _jsx(_components.p, {
        children: "Configure send event tracking on a channel when you want AI Decisioning to match recommendations with delivery events from your warehouse."
      }), "\n", _jsxs(_components.p, {
        children: ["Send event tracking is required for delivery-related reporting such as ", _jsx(_components.a, {
          href: "/ai-decisioning/insights#7-send-success",
          children: "send success"
        }), ", ", _jsx(_components.a, {
          href: "/ai-decisioning/insights#8-send-comparison",
          children: "send comparison"
        }), ", and ", _jsx(_components.a, {
          href: "/ai-decisioning/insights",
          children: "channel send success"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To configure it:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Configuration > Channels"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select a channel."
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Send event tracking"
          }), ", select the event model that represents sends."]
        }), "\n", _jsx(_components.li, {
          children: "Configure any additional matching or attribution settings needed for your data."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-insights-send-event-tracking.png",
          alt: "Send event tracking configuration panel"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Without send event tracking, delivery-related Insights views can't report send\nstatus for that channel."
        })
      }), "\n", _jsx(_components.h3, {
        id: "review-configuration-changes",
        children: "Review configuration changes"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Activity"
        }), " to see a chronological log of configuration changes across AI Decisioning, such as updated timing, edited outcome filters, renamed agents, and new messages. Each entry records who made the change and when, and ", _jsx(_components.strong, {
          children: "Details"
        }), " shows what changed."]
      }), "\n", _jsxs(_components.p, {
        children: ["Filter the log by scope to focus on ", _jsx(_components.strong, {
          children: "Global configuration"
        }), " or a specific agent."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-activity-tab.png",
          alt: "Activity log in configuration UI"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "agent-configuration",
        children: "Agent configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Open an agent's ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab for settings that apply only to that agent."]
      }), "\n", _jsxs(_components.p, {
        children: ["You select a ", _jsx(_components.strong, {
          children: "parent model"
        }), " for each agent when you ", _jsx(_components.a, {
          href: "/ai-decisioning/agents#create-a-new-agent",
          children: "create it"
        }), ". Agents in the same decision engine must use parent models from the same data source. For more information, see ", _jsx(_components.a, {
          href: "/ai-decisioning/prepare-data",
          children: "Prepare data for AI Decisioning"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "control-who-the-agent-can-evaluate",
        children: "Control who the agent can evaluate"
      }), "\n", _jsx(_components.p, {
        children: "Configure the agent's target audience and eligibility conditions. Agent eligibility applies in addition to shared channel eligibility and any message-level eligibility."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-agent-eligibility.png",
          alt: "Eligibility settings"
        })
      }), "\n", _jsx(_components.h3, {
        id: "define-goals-and-select-data",
        children: "Define goals and select data"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Goals"
        }), " to define the outcomes the agent should optimize when selecting messages. Messages inherit these goals and can't change them independently."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Feature matrix"
        }), " to select which feature matrix the agent uses. The feature matrix must match the agent's parent model."]
      }), "\n", _jsx(_components.h3, {
        id: "control-what-data-is-sent-downstream",
        children: "Control what data is sent downstream"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, trigger sync payloads include all columns from the agent's parent model. Use ", _jsx(_components.strong, {
          children: "Synced user columns"
        }), " to limit which parent-model fields are included. Use ", _jsx(_components.strong, {
          children: "Additional user columns"
        }), " when you need to include properties from related models."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-agent-synced-user-columns.png",
          alt: "Synced user columns on agent Configuration tab"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-agent-specific-delivery-limits",
        children: "Set agent-specific delivery limits"
      }), "\n", _jsx(_components.p, {
        children: "Configure agent-level frequency limits, scheduling, and time zone behavior when the restrictions should apply only to this agent. Shared scheduling rules still apply."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Time zone column"
          }), " — A parent-model field containing a UTC offset or IANA time zone."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Fallback time zone"
          }), " — The time zone used when no user-level value is available. If left blank, the fallback is ", _jsx(_components.code, {
            children: "America/Los_Angeles"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/configuration/aid-agent-time-zone.png",
          alt: "Time zone on agent Configuration tab"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/ai-decisioning/smart-suppression",
          children: "Smart suppression"
        }), " to reduce low-impact sends based on predicted user value."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more details on agent settings, see ", _jsx(_components.a, {
          href: "/ai-decisioning/agents",
          children: "Agents"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "message-configuration",
        children: "Message configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Open a message's ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab for settings that apply only to one message."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use message eligibility when a condition should apply to a particular message rather than the entire agent or channel. Use ", _jsx(_components.a, {
          href: "/ai-decisioning/tags",
          children: "tags"
        }), " to categorize messages for learning and analysis."]
      }), "\n", _jsx(_components.p, {
        children: "Messages inherit goals from their parent agent. To change the goals a message contributes toward, update the agent."
      }), "\n", _jsxs(_components.p, {
        children: ["For more details on message settings, see ", _jsx(_components.a, {
          href: "/ai-decisioning/messages",
          children: "Messages"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.a, {
              href: "/ai-decisioning/prepare-data",
              children: "Prepare data for AI Decisioning"
            }), " to model the warehouse data your agents evaluate."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Configure an AI Decisioning destination to deliver messages:"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/sfmc",
                children: "Salesforce Marketing Cloud"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/braze",
                children: "Braze"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/iterable",
                children: "Iterable"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
